import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard, Map, Users, Building2, Briefcase, Receipt,
  Wallet, UserCog, Heart, User, Settings, LogOut, ChevronDown,
  X, MoreHorizontal, Landmark, HandHeart, Plane, Search, Bot, Mail,
} from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import AlertsBell from '@/components/AlertsBell';
import GlobalSearch from '@/components/GlobalSearch';
import { useOperatorName } from '@/hooks/useOperatorName';

const POLLEN_ROLES = ['Pollen Dots User', 'Super Admin', 'Admin', 'Operator', 'Intern'];

// Pollen Dots primary navigation. Order matches the operational flow
// (overview → execution → people → money → identity).
const navItems = [
  { path: '/pollen-dots-dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/pollen-dots/my', label: 'Profile', icon: Heart },
  { path: '/pollen-dots/tours', label: 'Tours', icon: Briefcase },
  { path: '/pollen-dots/customers', label: 'Customers', icon: Users },
  { path: '/pollen-dots/vendors', label: 'Vendors', icon: Building2 },
  { path: '/pollen-dots/care', label: 'Dots of Care', icon: HandHeart },
  { path: '/pollen-dots/locations', label: 'Locations', icon: Map },
  { path: '/pollen-dots/payments', label: 'Payments', icon: Receipt },
  { path: '/pollen-dots/financials', label: 'Financials', icon: Wallet },
  { path: '/pollen-dots/team', label: 'Team', icon: UserCog },
  { path: '/pollen-dots/investors', label: 'Investors', icon: Landmark },
  { path: '/pollen-dots/concepts', label: 'Tour Concepts', icon: Plane },
  { path: '/pollen-dots/seo', label: 'SEO', icon: Search },
  { path: '/pollen-dots/aeo', label: 'AI Visibility', icon: Bot },
  { path: '/pollen-dots/mailbox', label: 'Mailbox', icon: Mail },
];

// Bottom nav: most-used items for mobile
const bottomNavItems = [
  { path: '/pollen-dots-dashboard', label: 'Home', icon: LayoutDashboard },
  { path: '/pollen-dots/tours', label: 'Tours', icon: Briefcase },
  { path: '/pollen-dots/customers', label: 'People', icon: Users },
  { path: '/pollen-dots/vendors', label: 'Vendors', icon: Building2 },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const isPollenUser = POLLEN_ROLES.includes(user?.role);
  const canManageUsers = ['Admin', 'Pollen Dots User', 'Super Admin'].includes(user?.role);
  // Surface the assigned operator name for shared inboxes
  // (e.g. team@pollendots.com → Rishita Dey) instead of the generic
  // Google account label. Falls back to the Google name when no mapping.
  const displayName = useOperatorName(user?.email, user?.name);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isActive = (path) => {
    if (path === '/pollen-dots-dashboard') {
      return location.pathname === '/pollen-dots-dashboard' || location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="min-h-screen bg-[#FDFDFC]" data-testid="app-layout">
      {/* Desktop Sidebar - hidden on mobile */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-border z-40 hidden lg:block" data-testid="sidebar">
        <div className="flex flex-col h-full">
          <div className="p-5 border-b border-border">
            <NavLink to="/pollen-dots-dashboard" className="flex items-center justify-center" data-testid="brand-link">
              <img src="/pollen-logo.png" alt="Pollen Dots Digital Passport" className="h-10 w-auto object-contain" />
            </NavLink>
            <p className="mt-2 text-center text-[9px] font-medium uppercase tracking-[0.22em] text-amber-700/70">
              Travel &middot; Connect &middot; Care
            </p>
          </div>

          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive: navIsActive }) =>
                      `sidebar-link ${(navIsActive || isActive(item.path)) ? 'active' : 'text-muted-foreground hover:text-foreground'}`
                    }
                    data-testid={`nav-${item.path.replace(/^\/pollen-dots\/?/, 'pd-').replace(/^\//, '')}`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>

            {canManageUsers && (
              <div className="mt-6 pt-6 border-t border-border">
                <p className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Admin</p>
                <NavLink
                  to="/admin"
                  className={({ isActive: a }) =>
                    `sidebar-link ${a ? 'active' : 'text-muted-foreground hover:text-foreground'}`
                  }
                  data-testid="nav-admin"
                >
                  <Settings className="w-4 h-4" />
                  <span>User Management</span>
                </NavLink>
              </div>
            )}
          </nav>

          <div className="p-4 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors" data-testid="user-menu-trigger">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} alt={displayName} />
                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                      {getInitials(displayName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground">{user?.role}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/pollen-dots/my')} data-testid="my-profile-menu-item">
                  <User className="w-4 h-4 mr-2" />
                  My Pollen Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                  <UserCog className="w-4 h-4 mr-2" />
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pb-20 lg:pb-0">
        <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-border lg:border-b-0 flex items-center justify-between gap-2 px-4 py-2 lg:px-6">
          <NavLink to="/pollen-dots-dashboard" className="flex items-center lg:hidden" data-testid="mobile-brand-link">
            <img src="/pollen-logo.png" alt="Pollen Dots Digital Passport" className="h-7 w-auto" />
          </NavLink>
          <div className="hidden lg:block" />
          {isPollenUser && <GlobalSearch />}
          {isPollenUser && <AlertsBell />}
          {isPollenUser && <NotificationBell />}
        </div>
        <div className="p-4 sm:p-6 lg:p-8 pt-0 lg:pt-4">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-border lg:hidden safe-area-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-16">
          {bottomNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className="flex flex-col items-center justify-center flex-1 h-full min-w-[56px] touch-target"
              data-testid={`mobile-nav-${item.path.replace(/^\/pollen-dots\/?/, 'pd-').replace(/^\//, '')}`}
            >
              <item.icon className={`w-5 h-5 ${isActive(item.path) ? 'text-primary' : 'text-muted-foreground'}`} />
              <span className={`text-[10px] mt-0.5 font-medium ${isActive(item.path) ? 'text-primary' : 'text-muted-foreground'}`}>
                {item.label}
              </span>
            </NavLink>
          ))}
          <button
            onClick={() => setMoreOpen(true)}
            className="flex flex-col items-center justify-center flex-1 h-full min-w-[56px] touch-target"
            data-testid="mobile-nav-more"
          >
            <MoreHorizontal className={`w-5 h-5 ${moreOpen ? 'text-primary' : 'text-muted-foreground'}`} />
            <span className={`text-[10px] mt-0.5 font-medium ${moreOpen ? 'text-primary' : 'text-muted-foreground'}`}>More</span>
          </button>
        </div>
      </nav>

      {/* Mobile More Menu Overlay */}
      {moreOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[60] lg:hidden" onClick={() => setMoreOpen(false)} />
          <div className="fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-2xl lg:hidden safe-area-bottom animate-slide-up" data-testid="mobile-more-menu">
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <h3 className="font-semibold text-foreground">Navigation</h3>
              <button onClick={() => setMoreOpen(false)} className="p-2 rounded-full hover:bg-muted">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-3 pb-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-3 gap-2 mb-3">
                {navItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setMoreOpen(false); }}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-colors touch-target ${
                      isActive(item.path) ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-muted-foreground'
                    }`}
                    data-testid={`more-nav-${item.path.replace(/^\/pollen-dots\/?/, 'pd-').replace(/^\//, '')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="text-xs font-medium text-center leading-tight">{item.label}</span>
                  </button>
                ))}
              </div>

              <div className="border-t pt-3 space-y-1">
                {canManageUsers && (
                  <button
                    onClick={() => { navigate('/admin'); setMoreOpen(false); }}
                    className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-muted text-muted-foreground touch-target"
                    data-testid="mobile-nav-admin"
                  >
                    <Settings className="w-5 h-5" />
                    <span className="text-sm font-medium">User Management</span>
                  </button>
                )}
                <button
                  onClick={() => { navigate('/pollen-dots/my'); setMoreOpen(false); }}
                  className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-muted touch-target"
                  data-testid="mobile-nav-my-profile"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} alt={displayName} />
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">{getInitials(displayName)}</AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="text-sm font-medium">{displayName}</p>
                    <p className="text-xs text-muted-foreground">{user?.role}</p>
                  </div>
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-muted text-destructive touch-target"
                  data-testid="mobile-logout"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-sm font-medium">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Layout;
